import { Box, Stack } from "@mui/material";
import { useFilteredTransactions } from "Api/Queries/Transactions/useFilteredTransactions";
import { useSearchTransactions } from "Api/Queries/Transactions/useSearchTransactions";
import { TransactionsList } from "Components/Shared/Transactions/TransactionsList/TransactionsList";
import { useMemo } from "react";
import { TransactionFilterChips } from "Components/Transactions/TransactionsSearchBar/TransactionFilters/TransactionFilterChips";
import { TransactionFilterBar } from "Components/Transactions/TransactionsSearchBar/TransactionsFilterBar";
import { useTransactionFilterManager } from "Components/Transactions/hooks/useTransactionFilterManager";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";

type Props = {
  initialLimit?: number;
};

export const CustomerTransactionsPageContent = ({
  initialLimit = 10,
}: Props) => {
  const filterManager = useTransactionFilterManager({
    variant: "customer",
    storageKey: LocalStorageKeys.CustomerTransactionFilters,
  });

  const { filters, availableFilters, searchPublicID, activeFilterCount } =
    filterManager;
  const isSearching = !!searchPublicID;

  // Filtered transactions - used when NOT searching
  const filteredTransactionsQuery = useFilteredTransactions({
    filters,
    limit: initialLimit,
  });

  // Search transactions - used when searching (ignores filters)
  const searchQuery = useSearchTransactions({
    searchPublicID,
    limit: initialLimit,
  });

  // Select which query results to display
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    isSearching ? searchQuery : filteredTransactionsQuery;

  const transactions = useMemo(
    () => data?.pages?.flatMap(({ items }) => items) ?? [],
    [data],
  );

  const totalCount = data?.pages?.[0]?.totalCount ?? 0;

  return (
    <Stack gap={3}>
      <TransactionFilterChips
        filters={filters}
        availableFilters={availableFilters}
        isVisible={activeFilterCount > 0}
      />
      <TransactionFilterBar filterManager={filterManager} />
      <Box mt={2}>
        <TransactionsList
          variant="customer"
          initialLimit={initialLimit}
          transactions={transactions}
          totalCount={totalCount}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isLoadingTransactions={isLoading}
          skeletonsCount={isSearching ? 0 : undefined}
        />
      </Box>
    </Stack>
  );
};
